import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Calculator, Loader2, Sparkles, GraduationCap, Lock, Search, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const TAG_COLORS = {
  Dream: 'bg-purple-100 text-purple-700 border-purple-200',
  Target: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Safety: 'bg-amber-100 text-amber-700 border-amber-200',
};

const CollegePredictor = () => {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [exam, setExam] = useState('JEE');
  const [rank, setRank] = useState('');
  const [predicting, setPredicting] = useState(false);
  const [results, setResults] = useState(null);

  const fetchStatus = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/college-predictor/status`); setStatus(data); }
    catch (e) { console.error('Failed to load predictor status:', e); }
    setLoading(false);
  };
  useEffect(() => { fetchStatus(); }, []);

  const handleBuy = async () => {
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay failed to load'); setPaying(false); return; }
      const { data } = await axios.post(`${API_URL}/api/college-predictor/purchase`);
      const options = {
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: 'INR',
        name: data.name, description: data.description,
        prefill: { name: data.user.name, email: data.user.email },
        theme: { color: '#0EA5E9' },
        handler: async (resp) => {
          try {
            await axios.post(`${API_URL}/api/college-predictor/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('College Predictor unlocked for 1 year!');
            await refreshUser();
            await fetchStatus();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPaying(false);
    }
  };

  const handlePredict = async () => {
    if (!rank || parseInt(rank) <= 0) { toast.error('Enter a valid rank'); return; }
    setPredicting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/college-predictor/predict`, { rank: parseInt(rank), exam });
      setResults(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Prediction failed');
    } finally {
      setPredicting(false);
    }
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="flex items-center justify-center py-32"><Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin" /></div></div>;

  const unlocked = status?.active;

  return (
    <div className="min-h-screen" data-testid="college-predictor-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-4">
            <GraduationCap className="w-4 h-4" /> College Predictor
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="predictor-title">
            Which college will my rank get me?
          </h1>
          <p className="text-base text-[#6B7280] max-w-2xl mx-auto">
            IIT / NIT / IIIT / AIIMS / state medical predictions — based on past 3-year cutoffs.
          </p>
        </div>

        {!unlocked ? (
          <div className="rounded-3xl p-7 bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white shadow-2xl text-center" data-testid="predictor-paywall">
            <Lock className="w-12 h-12 mx-auto mb-3 opacity-90" />
            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Unlock for ₹{status?.price_inr || 499}</h2>
            <p className="text-sm opacity-90 mb-5 max-w-md mx-auto">One-time payment, full year of unlimited predictions across JEE & NEET.</p>
            <button
              onClick={handleBuy}
              disabled={paying}
              className="px-7 py-4 bg-amber-400 hover:bg-amber-300 text-[#111827] rounded-2xl font-black shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition flex items-center gap-2 mx-auto disabled:opacity-60"
              data-testid="buy-predictor-button"
            >
              {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening checkout…</> : <><Sparkles className="w-5 h-5" /> Unlock Predictor · ₹499</>}
            </button>
            <p className="mt-3 text-xs opacity-80">Or <Link to="/upgrade" className="underline font-bold">go Pro at ₹299/mo</Link> — predictor included.</p>
          </div>
        ) : (
          <>
            <div className="rounded-3xl p-6 bg-white border border-slate-100 shadow-sm mb-6" data-testid="predictor-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
                <select value={exam} onChange={(e) => setExam(e.target.value)} className="px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none" data-testid="college-exam-select">
                  <option value="JEE">JEE (Engineering)</option>
                  <option value="NEET">NEET (Medical)</option>
                </select>
                <input type="number" value={rank} onChange={(e) => setRank(e.target.value)} placeholder="Your AIR rank" className="px-3 py-2 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none" data-testid="college-rank-input" />
                <button onClick={handlePredict} disabled={predicting} className="px-4 py-2 bg-[#0EA5E9] text-white rounded-xl font-bold hover:bg-[#0284C7] transition flex items-center justify-center gap-2 disabled:opacity-60" data-testid="college-predict-button">
                  {predicting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4" /> Predict</>}
                </button>
              </div>
            </div>

            {results && (
              <div data-testid="college-results">
                <div className="mb-4 text-sm text-[#6B7280]">
                  Found <strong className="text-[#111827]">{results.total_eligible}</strong> eligible colleges for AIR <strong className="text-[#0EA5E9]">{results.rank.toLocaleString()}</strong> in {results.exam}.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.colleges.map((c, idx) => (
                    <div key={idx} className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm" data-testid={`college-card-${idx}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{c.college}</h3>
                          <p className="text-xs text-[#6B7280] mt-0.5">{c.branch} · Cutoff ≤ {c.max_rank.toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${TAG_COLORS[c.category_tag]}`}>
                          {c.category_tag}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {results.colleges.length === 0 && (
                  <div className="p-8 rounded-2xl bg-amber-50 border border-amber-200 text-center">
                    <p className="text-amber-800">No colleges match this rank in our dataset. Try improving your rank or check NEET if you're a medical aspirant.</p>
                  </div>
                )}
                <p className="mt-4 text-xs text-[#9CA3AF] text-center">{results.note}</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default CollegePredictor;
